import type { TurnstileInstance } from "@marsidev/react-turnstile";
import { Turnstile } from "@marsidev/react-turnstile";
import { AlertCircle, CheckCircle2, Loader2, Send } from "lucide-react";
import {
  type FormEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { SelectWithOther } from "@/components/bugs/select-with-other.tsx";
import { ScreenshotDropZone } from "@/components/bugs/screenshot-drop-zone.tsx";
import { SessionLogDropZone } from "@/components/bugs/session-log-drop-zone.tsx";
import { Button } from "@/components/ui/button.tsx";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card.tsx";
import { Input } from "@/components/ui/input.tsx";
import { Label } from "@/components/ui/label.tsx";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.tsx";
import { Textarea } from "@/components/ui/textarea.tsx";
import { submitBugReport, useReleaseCatalog } from "@/lib/api.ts";
import { detectOperatingSystem } from "@/lib/os-detect.ts";
import type { SessionExport } from "@/lib/session-log.ts";

declare global {
  interface Window {
    __TURNSTILE_SITE_KEY__?: string;
  }
}

const TURNSTILE_SITE_KEY = import.meta.env.DEV
  ? "1x00000000000000000000AA"
  : (window.__TURNSTILE_SITE_KEY__ ?? "");

const categories = [
  { value: "gameplay", label: "Gameplay / Game Master" },
  { value: "ai-model", label: "AI / Model" },
  { value: "image-generation", label: "Image Generation" },
  { value: "exploration", label: "Exploration / Examination" },
  { value: "map-navigation", label: "Map / Navigation" },
  { value: "ui-interface", label: "UI / Interface" },
  { value: "audio", label: "Audio" },
  { value: "performance", label: "Performance" },
  { value: "save-load", label: "Save / Load" },
  { value: "visual-effects", label: "Visual Effects" },
  { value: "setup-installation", label: "Setup / Installation" },
  { value: "other", label: "Other" },
] as const;

const severities = [
  {
    value: "critical",
    label: "Critical",
    description: "Game crashes or data loss",
  },
  {
    value: "major",
    label: "Major",
    description: "Feature broken, no workaround",
  },
  {
    value: "minor",
    label: "Minor",
    description: "Feature impaired, workaround exists",
  },
  { value: "cosmetic", label: "Cosmetic", description: "Visual or text issue" },
] as const;

const platformOptions = [
  { value: "Windows 11", label: "Windows 11" },
  { value: "Windows 10", label: "Windows 10" },
  { value: "macOS", label: "macOS" },
  { value: "Linux", label: "Linux" },
  { value: "Linux (ARM64)", label: "Linux (ARM64)" },
] as const;

const gpuModelOptions = [
  // NVIDIA 50-series
  { value: "NVIDIA RTX 5090 (32 GB)", label: "NVIDIA RTX 5090 (32 GB)" },
  { value: "NVIDIA RTX 5080 (16 GB)", label: "NVIDIA RTX 5080 (16 GB)" },
  { value: "NVIDIA RTX 5070 Ti (16 GB)", label: "NVIDIA RTX 5070 Ti (16 GB)" },
  { value: "NVIDIA RTX 5070 (12 GB)", label: "NVIDIA RTX 5070 (12 GB)" },
  { value: "NVIDIA RTX 5060 Ti", label: "NVIDIA RTX 5060 Ti" },
  { value: "NVIDIA RTX 5060 (8 GB)", label: "NVIDIA RTX 5060 (8 GB)" },
  // NVIDIA 40-series
  { value: "NVIDIA RTX 4090 (24 GB)", label: "NVIDIA RTX 4090 (24 GB)" },
  { value: "NVIDIA RTX 4080 Super (16 GB)", label: "NVIDIA RTX 4080 Super (16 GB)" },
  { value: "NVIDIA RTX 4080 (16 GB)", label: "NVIDIA RTX 4080 (16 GB)" },
  { value: "NVIDIA RTX 4070 Ti Super (16 GB)", label: "NVIDIA RTX 4070 Ti Super (16 GB)" },
  { value: "NVIDIA RTX 4070 Ti (12 GB)", label: "NVIDIA RTX 4070 Ti (12 GB)" },
  { value: "NVIDIA RTX 4070 Super (12 GB)", label: "NVIDIA RTX 4070 Super (12 GB)" },
  { value: "NVIDIA RTX 4070 (12 GB)", label: "NVIDIA RTX 4070 (12 GB)" },
  { value: "NVIDIA RTX 4060 Ti", label: "NVIDIA RTX 4060 Ti" },
  { value: "NVIDIA RTX 4060 (8 GB)", label: "NVIDIA RTX 4060 (8 GB)" },
  // NVIDIA 30-series
  { value: "NVIDIA RTX 3090 (24 GB)", label: "NVIDIA RTX 3090 (24 GB)" },
  { value: "NVIDIA RTX 3080 (10 GB)", label: "NVIDIA RTX 3080 (10 GB)" },
  { value: "NVIDIA RTX 3070 (8 GB)", label: "NVIDIA RTX 3070 (8 GB)" },
  { value: "NVIDIA RTX 3060 (12 GB)", label: "NVIDIA RTX 3060 (12 GB)" },
  // Apple Silicon
  { value: "Apple M4 Max", label: "Apple M4 Max" },
  { value: "Apple M4 Pro", label: "Apple M4 Pro" },
  { value: "Apple M4", label: "Apple M4" },
  { value: "Apple M3 Max", label: "Apple M3 Max" },
  { value: "Apple M3 Pro", label: "Apple M3 Pro" },
  { value: "Apple M3", label: "Apple M3" },
  { value: "Apple M2 Max", label: "Apple M2 Max" },
  { value: "Apple M2 Pro", label: "Apple M2 Pro" },
  { value: "Apple M2", label: "Apple M2" },
  { value: "Apple M1 Max", label: "Apple M1 Max" },
  { value: "Apple M1 Pro", label: "Apple M1 Pro" },
  { value: "Apple M1", label: "Apple M1" },
  // AMD
  { value: "AMD RX 9070 XT (16 GB)", label: "AMD RX 9070 XT (16 GB)" },
  { value: "AMD RX 9070 (16 GB)", label: "AMD RX 9070 (16 GB)" },
  { value: "AMD RX 7900 XTX (24 GB)", label: "AMD RX 7900 XTX (24 GB)" },
  { value: "AMD RX 7900 XT (20 GB)", label: "AMD RX 7900 XT (20 GB)" },
  { value: "AMD RX 7800 XT (16 GB)", label: "AMD RX 7800 XT (16 GB)" },
  // Intel
  { value: "Intel Arc B580 (12 GB)", label: "Intel Arc B580 (12 GB)" },
  { value: "Intel Arc A770 (16 GB)", label: "Intel Arc A770 (16 GB)" },
  { value: "Intel Arc A750 (8 GB)", label: "Intel Arc A750 (8 GB)" },
] as const;

const gpuVramMap: Record<string, string[]> = {
  "NVIDIA RTX 5060 Ti": ["8 GB", "16 GB"],
  "NVIDIA RTX 4060 Ti": ["8 GB", "16 GB"],
  "Apple M4 Max": ["36 GB", "48 GB", "64 GB", "128 GB"],
  "Apple M4 Pro": ["24 GB", "48 GB"],
  "Apple M4": ["16 GB", "24 GB"],
  "Apple M3 Max": ["36 GB", "48 GB", "64 GB", "128 GB"],
  "Apple M3 Pro": ["18 GB", "36 GB"],
  "Apple M3": ["8 GB", "16 GB", "24 GB"],
  "Apple M2 Max": ["32 GB", "64 GB", "96 GB"],
  "Apple M2 Pro": ["16 GB", "32 GB"],
  "Apple M2": ["8 GB", "16 GB", "24 GB"],
  "Apple M1 Max": ["32 GB", "64 GB"],
  "Apple M1 Pro": ["16 GB", "32 GB"],
  "Apple M1": ["8 GB", "16 GB"],
};

const textModelOptions = [
  { value: "None", label: "None loaded" },
  // Gemma 4 E2B UQFF (2.3B)
  { value: "Gemma 4 E2B Q2K UQFF", label: "Gemma 4 E2B Q2K UQFF" },
  { value: "Gemma 4 E2B Q3K UQFF", label: "Gemma 4 E2B Q3K UQFF" },
  { value: "Gemma 4 E2B Q4K UQFF", label: "Gemma 4 E2B Q4K UQFF" },
  { value: "Gemma 4 E2B Q5K UQFF", label: "Gemma 4 E2B Q5K UQFF" },
  { value: "Gemma 4 E2B Q6K UQFF", label: "Gemma 4 E2B Q6K UQFF" },
  { value: "Gemma 4 E2B Q8_0 UQFF", label: "Gemma 4 E2B Q8_0 UQFF" },
  { separator: true },
  // Gemma 4 E4B UQFF (4.3B)
  { value: "Gemma 4 E4B Q2K UQFF", label: "Gemma 4 E4B Q2K UQFF" },
  { value: "Gemma 4 E4B Q3K UQFF", label: "Gemma 4 E4B Q3K UQFF" },
  { value: "Gemma 4 E4B Q4K UQFF", label: "Gemma 4 E4B Q4K UQFF" },
  { value: "Gemma 4 E4B Q5K UQFF", label: "Gemma 4 E4B Q5K UQFF" },
  { value: "Gemma 4 E4B Q6K UQFF", label: "Gemma 4 E4B Q6K UQFF" },
  { value: "Gemma 4 E4B Q8_0 UQFF", label: "Gemma 4 E4B Q8_0 UQFF" },
  { value: "Don't know", label: "I don't know" },
] as const;

const imageModelOptions = [
  { value: "None", label: "None loaded" },
  // FLUX.1 Schnell GGUF
  { value: "FLUX.1 Schnell Q2_K GGUF", label: "FLUX.1 Schnell Q2_K GGUF" },
  { value: "FLUX.1 Schnell Q3_K GGUF", label: "FLUX.1 Schnell Q3_K GGUF" },
  { value: "FLUX.1 Schnell Q4_0 GGUF", label: "FLUX.1 Schnell Q4_0 GGUF" },
  { value: "FLUX.1 Schnell Q4_K GGUF", label: "FLUX.1 Schnell Q4_K GGUF" },
  { value: "FLUX.1 Schnell Q8_0 GGUF", label: "FLUX.1 Schnell Q8_0 GGUF" },
  { value: "Don't know", label: "I don't know" },
] as const;

function defaultPlatform(): string {
  const os = detectOperatingSystem();
  switch (os) {
    case "windows":
      return "Windows 11";
    case "macos":
      return "macOS";
    case "linux":
      return "Linux";
    default:
      return "";
  }
}

type FormStatus = "idle" | "submitting" | "success" | "error";

export function BugReportForm() {
  const turnstileRef = useRef<TurnstileInstance | null>(null);
  const { data: releases } = useReleaseCatalog();

  const versionOptions = (releases ?? []).map((r) => {
    const v = r.tagName.replace(/^v/, "");
    return { value: v, label: v };
  });
  const latestVersion = versionOptions[0]?.value ?? "";

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [severity, setSeverity] = useState("");
  const [gameVersion, setGameVersion] = useState("");
  const [platform, setPlatform] = useState(defaultPlatform);
  const [gpuModel, setGpuModel] = useState("");
  const [gpuVram, setGpuVram] = useState("");
  const [textModel, setTextModel] = useState("");
  const [imageModel, setImageModel] = useState("");
  const [reporterName, setReporterName] = useState("");
  const [reporterContact, setReporterContact] = useState("");
  const [sessionLog, setSessionLog] = useState<SessionExport | null>(null);
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  useEffect(() => {
    if (latestVersion && gameVersion === "") setGameVersion(latestVersion);
  }, [latestVersion, gameVersion]);

  useEffect(() => {
    if (!gpuVramMap[gpuModel]) {
      setGpuVram("");
    } else if (gpuVram && !gpuVramMap[gpuModel].includes(gpuVram)) {
      setGpuVram("");
    }
  }, [gpuModel]);

  useEffect(() => {
    const MAX_SCREENSHOT_SIZE = 4 * 1024 * 1024;

    function handlePaste(e: ClipboardEvent) {
      const active = document.activeElement;
      const isTyping =
        active instanceof HTMLInputElement ||
        active instanceof HTMLTextAreaElement ||
        active?.getAttribute("contenteditable") === "true";

      const files = e.clipboardData?.files;
      if (!files || files.length === 0) return;

      const imageFile = Array.from(files).find((f) => f.type.startsWith("image/"));
      if (!imageFile) return;

      if (isTyping && e.clipboardData?.types.includes("text/plain")) {
        return;
      }

      e.preventDefault();

      if (imageFile.size > MAX_SCREENSHOT_SIZE) {
        setErrorMessage("Pasted image exceeds the 4 MB size limit.");
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        if (result.startsWith("data:image/")) {
          setScreenshot(result);
          setErrorMessage("");
        }
      };
      reader.readAsDataURL(imageFile);
    }

    document.addEventListener("paste", handlePaste);
    return () => document.removeEventListener("paste", handlePaste);
  }, []);

  const [status, setStatus] = useState<FormStatus>("idle");
  const [errorMessage, setErrorMessage] = useState("");

  const canSubmit =
    status !== "submitting" &&
    title.trim() !== "" &&
    category !== "" &&
    reporterName.trim() !== "" &&
    gameVersion.trim() !== "" &&
    platform.trim() !== "" &&
    turnstileToken !== null;

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      if (!canSubmit) return;

      setStatus("submitting");
      setErrorMessage("");

      try {
        const gpu = gpuModel.trim()
          ? gpuVram && gpuVramMap[gpuModel]
            ? `${gpuModel} (${gpuVram})`
            : gpuModel
          : undefined;
        await submitBugReport({
          title: title.trim(),
          description: description.trim(),
          category,
          severity,
          gameVersion: gameVersion.trim() || undefined,
          platform: platform.trim() || undefined,
          gpuInfo: gpu,
          modelInfo:
            [textModel.trim(), imageModel.trim()].filter(Boolean).join(" + ") ||
            undefined,
          reporterName: reporterName.trim() || undefined,
          reporterContact: reporterContact.trim() || undefined,
          sessionLog: sessionLog ? JSON.stringify(sessionLog) : null,
          screenshot,
          turnstileToken: turnstileToken as string,
        });
        setStatus("success");
      } catch (err) {
        setStatus("error");
        setErrorMessage(
          err instanceof Error ? err.message : "Something went wrong.",
        );
        turnstileRef.current?.reset();
        setTurnstileToken(null);
      }
    },
    [
      canSubmit,
      title,
      description,
      category,
      severity,
      gameVersion,
      platform,
      gpuModel,
      gpuVram,
      textModel,
      imageModel,
      reporterName,
      reporterContact,
      sessionLog,
      screenshot,
      turnstileToken,
    ],
  );

  const handleReset = useCallback(() => {
    setTitle("");
    setDescription("");
    setCategory("");
    setSeverity("");
    setGameVersion(latestVersion);
    setPlatform(defaultPlatform());
    setGpuModel("");
    setGpuVram("");
    setTextModel("");
    setImageModel("");
    setReporterName("");
    setReporterContact("");
    setSessionLog(null);
    setScreenshot(null);
    setStatus("idle");
    setErrorMessage("");
    turnstileRef.current?.reset();
    setTurnstileToken(null);
  }, []);

  if (status === "success") {
    return (
      <Card className="border-primary/30">
        <CardContent className="flex flex-col items-center gap-4 py-12 text-center">
          <CheckCircle2 className="h-12 w-12 text-primary" />
          <div>
            <p className="font-semibold text-lg">Bug report submitted</p>
            <p className="mt-1 text-muted-foreground text-sm">
              Thank you for helping improve Reify.
            </p>
          </div>
          <Button variant="outline" onClick={handleReset}>
            Submit another report
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* ── Bug details ── */}
      <Card>
        <CardHeader>
          <CardTitle>Bug Details</CardTitle>
          <CardDescription>
            Describe what happened and how to reproduce it.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              placeholder="Short summary of the issue"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="reporter-name">Your name/handle *</Label>
            <Input
              id="reporter-name"
              placeholder="Your name or handle"
              value={reporterName}
              onChange={(e) => setReporterName(e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              placeholder="What happened? What did you expect? Steps to reproduce…"
              rows={5}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Category *</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Severity</Label>
              <Select value={severity} onValueChange={setSeverity}>
                <SelectTrigger>
                  <SelectValue placeholder="Select severity" />
                </SelectTrigger>
                <SelectContent>
                  {severities.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      <span>{s.label}</span>
                      <span className="ml-2 text-muted-foreground text-xs">
                        — {s.description}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* ── System info ── */}
        <Card>
          <CardHeader>
            <CardTitle>System Information</CardTitle>
            <CardDescription>
              Help us reproduce by sharing your setup.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="space-y-2">
              <Label>Game Version *</Label>
              <SelectWithOther
                options={versionOptions}
                value={gameVersion}
                onValueChange={setGameVersion}
                placeholder="Select version"
                otherPlaceholder="e.g. 0.6.0"
              />
            </div>
            <div className="space-y-2">
              <Label>Platform *</Label>
              <SelectWithOther
                options={platformOptions}
                value={platform}
                onValueChange={setPlatform}
                placeholder="Select platform"
                otherPlaceholder="e.g. Windows 11 ARM"
              />
            </div>
            <div className="space-y-2">
              <Label>GPU</Label>
              <SelectWithOther
                options={gpuModelOptions}
                value={gpuModel}
                onValueChange={setGpuModel}
                placeholder="Select GPU"
                otherPlaceholder="e.g. GTX 1080 Ti (11 GB)"
              />
            </div>
            {gpuVramMap[gpuModel] && (
              <div className="space-y-2">
                <Label>VRAM</Label>
                <Select value={gpuVram} onValueChange={setGpuVram}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select VRAM" />
                  </SelectTrigger>
                  <SelectContent>
                    {gpuVramMap[gpuModel].map((v) => (
                      <SelectItem key={v} value={v}>
                        {v}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label>Text Transformer</Label>
              <SelectWithOther
                options={textModelOptions}
                value={textModel}
                onValueChange={setTextModel}
                placeholder="Select text transformer"
                otherPlaceholder="e.g. Llama 3 8B Q4_K"
              />
            </div>
            <div className="space-y-2">
              <Label>Image Transformer</Label>
              <SelectWithOther
                options={imageModelOptions}
                value={imageModel}
                onValueChange={setImageModel}
                placeholder="Select image transformer"
                otherPlaceholder="e.g. SDXL Turbo"
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex flex-col gap-6">
          {/* ── Screenshot ── */}
          <Card>
            <CardHeader>
              <CardTitle>Screenshot</CardTitle>
              <CardDescription>
                Optional — attach a screenshot to help us reproduce the issue.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ScreenshotDropZone value={screenshot} onChange={setScreenshot} />
            </CardContent>
          </Card>

          {/* ── Session log ── */}
          <Card>
            <CardHeader>
              <CardTitle>Session Log</CardTitle>
              <CardDescription>
                Optional — attach the gameplay session log exported from Reify.
                In the game, go to{" "}
                <span className="font-medium text-foreground">
                  Settings → Gameplay → Download Session Log
                </span>{" "}
                to save the JSON file, then drop it here.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <SessionLogDropZone value={sessionLog} onChange={setSessionLog} />
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ── Contact ── */}
      <Card>
        <CardHeader>
          <CardTitle>Contact Info</CardTitle>
          <CardDescription>
            Optional — in case we need to follow up. If we've already got your
            contact details from a previous report, just use the same name/handle
            and you don't need to add it again.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label htmlFor="reporter-contact">Email or Discord</Label>
            <Input
              id="reporter-contact"
              placeholder="you@example.com or user#1234"
              value={reporterContact}
              onChange={(e) => setReporterContact(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* ── Submit ── */}
      <div className="flex flex-col items-start gap-4">
        <Turnstile
          ref={turnstileRef}
          siteKey={TURNSTILE_SITE_KEY}
          options={{ theme: "dark", size: "normal" }}
          onSuccess={setTurnstileToken}
          onExpire={() => setTurnstileToken(null)}
        />

        {status === "error" && (
          <div className="flex items-center gap-2 text-destructive text-sm">
            <AlertCircle className="h-4 w-4" />
            {errorMessage}
          </div>
        )}

        <Button type="submit" disabled={!canSubmit} size="lg">
          {status === "submitting" ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Submitting…
            </>
          ) : (
            <>
              <Send className="mr-2 h-4 w-4" />
              Submit Bug Report
            </>
          )}
        </Button>
      </div>
    </form>
  );
}
